import React, { useRef, useEffect, useState } from 'react';
import { useWallet } from '../contexts/WalletContext';
import { useAuth } from '../contexts/AuthContext';
import jazzicon from '@metamask/jazzicon';
import ThemeToggle from './ThemeToggle';
import WalletSelector from './WalletSelector';
import './Header.css';

interface HeaderProps {
  subtitle?: string;
}

const Header: React.FC<HeaderProps> = ({ subtitle }) => {
  const { isConnected, account, connectWalletWithProvider, connectFreighter, disconnectWallet, error, loading } = useWallet();
  const { email, clientId, isAuthenticated, signOut } = useAuth();
  const iconRef = useRef<HTMLDivElement>(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);
  const [isWalletSelectorOpen, setIsWalletSelectorOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const truncateMiddle = (str: string, head = 6, tail = 4): string => {
    if (!str || str.length <= head + tail) return str;
    return `${str.slice(0, head)}…${str.slice(-tail)}`;
  };

  const handleWalletAction = () => {
    if (isConnected) {
      disconnectWallet();
    } else {
      setIsWalletSelectorOpen(true);
    }
  };

  const handleWalletSelect = async (provider: any, providerName: string) => {
    try {
      if (providerName === 'Freighter' || provider?.__stellar) {
        await connectFreighter();
      } else {
        await connectWalletWithProvider(provider, providerName);
      }
      setIsWalletSelectorOpen(false);
    } catch (err) {
      console.error('Failed to connect with selected provider:', err);
    }
  };

  const toggleHeader = () => {
    setIsHeaderExpanded(!isHeaderExpanded);
  };

  const handleCopyClientId = async () => {
    if (!clientId) return;
    try {
      await navigator.clipboard.writeText(clientId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy client ID:', err);
    }
  };

  useEffect(() => {
    if (account && iconRef.current) {
      iconRef.current.innerHTML = '';
      let seed: number;
      if (account.startsWith('0x') && account.length >= 10) {
        seed = parseInt(account.slice(2, 10), 16);
      } else {
        seed = Array.from(account).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
      }
      if (!isNaN(seed)) {
        const icon = jazzicon(24, seed);
        iconRef.current.appendChild(icon);
      }
    }
  }, [account]);

  return (
    <header>
      <div className="header-content">
        <div className="header-title-section">
          <h1>
            trustline{subtitle && ` - ${subtitle}`}
          </h1>
          <button onClick={toggleHeader} className="header-toggle-btn" aria-label="Toggle header">
            <span className="desktop-arrow">{isHeaderExpanded ? '◄' : '►'}</span>
            <span className="mobile-arrow">{isHeaderExpanded ? '▲' : '▼'}</span>
          </button>
        </div>
        <div className={`wallet-section ${isHeaderExpanded ? 'expanded' : 'collapsed'}`}>
          {isConnected ? (
            <div className="wallet-info">
              <div className="account-display">
                <div ref={iconRef} className="account-icon"></div>
                <span className="account">{account.slice(0, 6)}...{account.slice(-4)}</span>
              </div>
              <button onClick={handleWalletAction} className="wallet-btn disconnect">
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={handleWalletAction}
              className="wallet-btn connect"
              disabled={loading}
            >
              {loading ? 'Connecting...' : 'Connect Wallet'}
            </button>
          )}
          {isAuthenticated && (
            <div className="account-info">
              <div
                className={`account-info-text ${clientId ? 'account-info-text-copyable' : ''}${
                  copied ? ' account-info-text-copied' : ''
                }`}
                onClick={clientId ? handleCopyClientId : undefined}
                role={clientId ? 'button' : undefined}
                title={clientId ? (copied ? 'Copied!' : 'Click to copy Client ID') : undefined}
                aria-label={clientId ? (copied ? 'Client ID copied' : 'Copy Client ID to clipboard') : undefined}
              >
                <span className="email-value">{email}</span>
                {clientId && (
                  <span className="client-id-value" title={clientId}>
                    ID: {truncateMiddle(clientId)}
                    {copied ? (
                      <svg className="client-id-copy-icon client-id-copy-icon-copied" viewBox="0 0 24 24" width="12" height="12" aria-hidden>
                        <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    ) : (
                      <svg className="client-id-copy-icon" viewBox="0 0 24 24" width="12" height="12" aria-hidden>
                        <path fill="currentColor" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                      </svg>
                    )}
                  </span>
                )}
              </div>
              <button onClick={() => signOut()} className="wallet-btn disconnect">
                Sign Out
              </button>
            </div>
          )}
          <ThemeToggle />
        </div>
      </div>
      {error && (
        <div className="header-error">
          {error}
        </div>
      )}

      <WalletSelector
        isOpen={isWalletSelectorOpen}
        onClose={() => setIsWalletSelectorOpen(false)}
        onSelectWallet={handleWalletSelect}
      />
    </header>
  );
};

export default Header;
